
import { rankingCards } from '../../../mocks/ranking-cards';

export default function RankingList() {
  // Filter high return rate cards (1.0% or higher)
  const highReturnCards = rankingCards.filter(card => {
    const rate = parseFloat(card.returnRate.replace('%', ''));
    return rate >= 1.0;
  }).slice(0, 3);

  const campaignInfo: Record<string, string> = {
    'mitsui': '今なら最大25,600円相当プレゼント',
    'rakuten': '今なら最大8,000ポイント進呈',
    'dcard': '今なら最大21,000ポイント進呈',
    'recruit': '新規入会で最大6,000ポイント',
    'jaccs': '今なら初年度年会費無料'
  };

  return (
    <section id="ranking" className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-[#003366] mb-6">
          高還元率カードおすすめランキング
        </h2>

        {/* 導入文 */}
        <div className="bg-gray-50 rounded-xl p-6 mb-12 max-w-4xl mx-auto">
          <p className="text-gray-700 leading-relaxed">
            高還元率カードを選ぶ際は、<strong>基本還元率</strong>だけでなく、<strong>よく使う店舗での還元率</strong>も重要です。
            コンビニやスーパーで最大7%還元、ネットショッピングで3%還元など、自分のライフスタイルに合ったカードを選びましょう。
            年間利用額が多い方ほど、還元率の差が大きな節約につながります。
          </p>
        </div>

        <div className="space-y-8">
          {highReturnCards.map((card, index) => (
            <div
              key={card.id}
              className={`rounded-xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden ${
                index === 0 
                  ? 'bg-gradient-to-r from-[#FFF9E6] to-[#FFF3CC] border-2 border-[#FFD700]' 
                  : 'bg-white border border-gray-200'
              }`}
            >
              {/* 1位バッジ */}
              {index === 0 && (
                <div className="bg-[#FFD700] text-[#003366] text-center py-2 font-bold text-sm">
                  <i className="ri-trophy-line mr-2"></i>
                  還元率No.1！最もポイントが貯まるカード
                </div>
              )}

              <div className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Rank Badge */}
                  <div className="flex-shrink-0">
                    <div className={`w-20 h-20 flex items-center justify-center rounded-full font-bold text-2xl ${
                      index === 0 ? 'bg-gradient-to-br from-[#FFD700] to-[#FFA500] text-white shadow-lg' :
                      index === 1 ? 'bg-gradient-to-br from-[#C0C0C0] to-[#A0A0A0] text-white' :
                      index === 2 ? 'bg-gradient-to-br from-[#CD7F32] to-[#B8860B] text-white' :
                      'bg-gray-200 text-gray-700'
                    }`}>
                      {index + 1}位
                    </div>
                  </div>

                  {/* Card Info */}
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-start gap-6 mb-6">
                      {/* Logo */}
                      <div className="w-48 h-32 flex items-center justify-center bg-white rounded-lg p-4 shadow-sm">
                        <img
                          src={card.logo}
                          alt={card.name}
                          className="w-full h-full object-contain"
                        />
                      </div>

                      {/* Details */}
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-[#003366] mb-4">{card.name}</h3>
                        
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="bg-white/80 rounded-lg p-3">
                            <p className="text-sm text-gray-600 mb-1">還元率</p>
                            <p className="text-xl font-bold text-[#DC2626]">{card.returnRate}</p>
                          </div>
                          <div className="bg-white/80 rounded-lg p-3">
                            <p className="text-sm text-gray-600 mb-1">年会費</p>
                            <p className="text-xl font-bold text-[#003366]">{card.annualFee}</p>
                          </div>
                        </div>

                        {/* Benefits */}
                        <div className="space-y-2 mb-4">
                          <p className="text-sm font-bold text-gray-700 mb-2">おすすめポイント</p>
                          {card.benefits.map((benefit, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <i className="ri-check-line text-[#FFB800] text-lg flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                              <p className="text-gray-700 text-sm">{benefit}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* キャンペーン訴求 */}
                    {campaignInfo[card.id] && (
                      <div className="bg-[#DC2626]/10 border border-[#DC2626]/30 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-2">
                          <i className="ri-gift-line text-[#DC2626] text-xl"></i>
                          <span className="font-bold text-[#DC2626]">{campaignInfo[card.id]}</span>
                        </div>
                      </div>
                    )}

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href={card.officialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold px-6 py-4 rounded-lg transition-all text-center cursor-pointer whitespace-nowrap shadow-lg"
                      >
                        <i className="ri-external-link-line mr-2"></i>
                        公式サイトで詳細を見る（PR）
                      </a>
                      <a
                        href={card.detailUrl}
                        className="flex-1 bg-white hover:bg-gray-50 text-[#003366] font-bold px-6 py-4 rounded-lg border-2 border-[#003366] transition-all text-center cursor-pointer whitespace-nowrap"
                      >
                        詳細を見る
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 選び方セクション */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-[#003366] mb-6">高還元率カードの選び方</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="font-bold text-[#003366] mb-3 flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-[#FFB800]"></i>
                メリット
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• 日常の買い物でポイントがザクザク貯まる</li>
                <li>• 年間数万円分のポイント還元も可能</li>
                <li>• 特定店舗で最大7%の高還元</li>
                <li>• 貯まったポイントは現金同様に使える</li>
              </ul>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="font-bold text-[#003366] mb-3 flex items-center gap-2">
                <i className="ri-error-warning-line text-[#DC2626]"></i>
                注意点
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• 還元率アップには条件がある場合も</li>
                <li>• ポイントの有効期限に注意</li>
                <li>• 年会費とのバランスを考慮</li>
                <li>• 使える店舗・サービスを確認</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
